import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { CalendarIcon, X } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { textStyles } from "@/lib/textStyles";
import {
  formatTimeInputValue,
  getAllowedFutureTimeInputValue,
  getDateOfBirthFromAge,
  getMinimumFutureTimeInputValue,
  isBeforeLocalToday,
  isPastOrCurrentDateTime,
  isSameLocalDate,
  toDateOnlyISO,
  toUTCISO,
} from "@/lib/dateUtils";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import {
  useCreatePatientMutation,
  useLazyCheckPatientExistsQuery,
} from "../../infrastructure/api/patientsApi";
import type { CreatePatientPayload } from "../../domain/entities/Patient";
import {
  useGetDoctorsQuery,
  useGetGroupsQuery,
} from "@/features/members-groups/infrastructure/api/membersGroupsApi";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";

interface AddPatientDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

const NAME_MAX_LENGTH = 50;
const NAME_DISALLOWED_CHARS = /[^A-Za-z0-9.]/g;

const sanitizeNameInput = (value: string) =>
  value.replace(NAME_DISALLOWED_CHARS, "").slice(0, NAME_MAX_LENGTH);

interface FormData {
  firstName: string;
  lastName: string;
  mobileNumber: string;
  email: string;
  gender: string;
  ethnicity: string;
  dateOfBirth: Date | undefined;
  age: string;
  emergencyContactName: string;
  emergencyContactMobile: string;
  assignedDoctor: string;
  appointmentDateTime: Date | undefined;
  recentTestId: string;
}

const initialFormData: FormData = {
  firstName: "",
  lastName: "",
  mobileNumber: "",
  email: "",
  gender: "",
  ethnicity: "",
  dateOfBirth: undefined,
  age: "",
  emergencyContactName: "",
  emergencyContactMobile: "",
  assignedDoctor: "",
  appointmentDateTime: undefined,
  recentTestId: "",
};

export const AddPatientDialog: React.FC<AddPatientDialogProps> = ({
  open,
  onOpenChange,
  onSuccess,
}) => {
  const { t } = useTranslation("patients");
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>(
    {},
  );
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [dobCalendarOpen, setDobCalendarOpen] = useState(false);
  const [dobCalendarMonth, setDobCalendarMonth] = useState<Date>(new Date());
  const [appointmentCalendarOpen, setAppointmentCalendarOpen] = useState(false);
  const [selectedGroups, setSelectedGroups] = useState<
    { id: string; name: string }[]
  >([]);

  const { data: groups = [], isLoading: isGroupsLoading } = useGetGroupsQuery(
    {},
  );
  const [checkPatientExists] = useLazyCheckPatientExistsQuery();
  const { data: groupDoctors = [], isLoading: isDoctorsLoading } =
    useGetDoctorsQuery(
      { group_ids: selectedGroups.map((g) => g.id) },
      { skip: selectedGroups.length === 0 },
    );

  const addGroup = (groupId: string) => {
    const group = groups.find((g) => g.id === groupId);
    if (group && !selectedGroups.some((g) => g.id === groupId)) {
      setSelectedGroups((prev) => [...prev, { id: group.id, name: group.name }]);
      setFormData((prev) => ({ ...prev, assignedDoctor: "" }));
    }
  };

  const removeGroup = (groupId: string) => {
    setSelectedGroups((prev) => prev.filter((g) => g.id !== groupId));
    setFormData((prev) => ({ ...prev, assignedDoctor: "" }));
  };

  const isFormValid =
    formData.firstName.trim() &&
    formData.lastName.trim() &&
    formData.mobileNumber.trim() &&
    formData.gender &&
    formData.ethnicity &&
    (formData.dateOfBirth || formData.age.trim()) &&
    selectedGroups.length > 0 &&
    formData.assignedDoctor &&
    !errors.email &&
    !errors.mobileNumber &&
    !errors.age &&
    !errors.emergencyContactName &&
    !errors.emergencyContactMobile;

  const [createPatient, { isLoading }] = useCreatePatientMutation();
  const isAppointmentDateToday =
    formData.appointmentDateTime !== undefined &&
    isSameLocalDate(formData.appointmentDateTime);
  const minimumFutureTime = getMinimumFutureTimeInputValue();

  const genderOptions = [
    { value: "Male", label: t("patientForm.genderOptions.male") },
    { value: "Female", label: t("patientForm.genderOptions.female") },
    { value: "Other", label: t("patientForm.genderOptions.other") },
  ];
  const ethnicityOptions = [
    {
      value: "American Indian or Alaska Native",
      label: t("patientForm.ethnicityOptions.americanIndian"),
    },
    { value: "Black or African", label: t("patientForm.ethnicityOptions.black") },
    { value: "East Asian", label: t("patientForm.ethnicityOptions.eastAsian") },
    { value: "South Asian", label: t("patientForm.ethnicityOptions.southAsian") },
    {
      value: "Southeast Asian",
      label: t("patientForm.ethnicityOptions.southeastAsian"),
    },
    {
      value: "Middle Eastern or North African",
      label: t("patientForm.ethnicityOptions.middleEastern"),
    },
    {
      value: "Native Hawaiian or Pacific Islander",
      label: t("patientForm.ethnicityOptions.nativeHawaiian"),
    },
    { value: "White or European", label: t("patientForm.ethnicityOptions.white") },
    { value: "Latino or Hispanic", label: t("patientForm.ethnicityOptions.latino") },
    { value: "Mixed or Multiracial", label: t("patientForm.ethnicityOptions.mixed") },
    { value: "Other", label: t("patientForm.ethnicityOptions.other") },
    {
      value: "Prefer not to say",
      label: t("patientForm.ethnicityOptions.preferNotToSay"),
    },
  ];

  const getCaregiverErrors = (
    name: string,
    mobile: string,
    requireFields = false,
  ): Partial<Record<keyof FormData, string>> => {
    const newErrors: Partial<Record<keyof FormData, string>> = {};
    const hasName = !!name.trim();
    const hasMobile = !!mobile.trim();
    const shouldValidate = requireFields || hasName || hasMobile;

    if (!shouldValidate) {
      return newErrors;
    }

    if (!hasName) {
      newErrors.emergencyContactName = t(
        "patientForm.validation.caregiverNameRequired",
      );
    }

    if (!hasMobile) {
      newErrors.emergencyContactMobile = t(
        "patientForm.validation.caregiverMobileRequired",
      );
    } else if (!isValidPhoneNumber(mobile)) {
      newErrors.emergencyContactMobile = t(
        "patientForm.validation.caregiverMobileInvalid",
      );
    }

    return newErrors;
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof FormData, string>> = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = t("patientForm.validation.firstNameRequired");
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = t("patientForm.validation.lastNameRequired");
    }

    if (!formData.mobileNumber) {
      newErrors.mobileNumber = t("patientForm.validation.mobileRequired");
    } else if (!isValidPhoneNumber(formData.mobileNumber)) {
      newErrors.mobileNumber = t("patientForm.validation.mobileInvalid");
    }

    if (
      formData.email.trim() &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())
    ) {
      newErrors.email = t("patientForm.validation.emailInvalid");
    }

    if (!formData.gender) {
      newErrors.gender = t("patientForm.validation.genderRequired");
    }

    if (!formData.ethnicity) {
      newErrors.ethnicity = t("patientForm.validation.ethnicityRequired");
    }

    if (!formData.dateOfBirth && !formData.age.trim()) {
      newErrors.dateOfBirth = t("patientForm.validation.dobOrAgeRequired");
    } else if (!formData.dateOfBirth && formData.age.trim()) {
      const ageValue = Number(formData.age);
      if (!Number.isInteger(ageValue) || ageValue <= 0 || ageValue > 120) {
        newErrors.age = t("patientForm.validation.ageInvalid");
      }
    }

    Object.assign(
      newErrors,
      getCaregiverErrors(
        formData.emergencyContactName,
        formData.emergencyContactMobile,
      ),
    );

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: keyof FormData, value: string) => {
    const nextValue =
      field === "firstName" || field === "lastName"
        ? sanitizeNameInput(value)
        : value;

    setFormData((prev) => ({
      ...prev,
      [field]: nextValue,
    }));

    if (field === "emergencyContactName") {
      setErrors((prev) => ({
        ...prev,
        emergencyContactName: undefined,
        emergencyContactMobile: undefined,
        ...getCaregiverErrors(
          value,
          formData.emergencyContactMobile,
          hasSubmitted,
        ),
      }));
      return;
    }

    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleEmailChange = (value: string) => {
    setFormData((prev) => ({ ...prev, email: value }));
    if (value.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())) {
      setErrors((prev) => ({
        ...prev,
        email: t("patientForm.validation.emailInvalid"),
      }));
    } else {
      setErrors((prev) => ({ ...prev, email: undefined }));
    }
  };

  const handlePhoneChange = async (
    field: "mobileNumber" | "emergencyContactMobile",
    val: string,
  ) => {
    // Update form state
    setFormData((prev) => ({
      ...prev,
      [field]: val,
    }));

    if (field === "emergencyContactMobile") {
      setErrors((prev) => ({
        ...prev,
        emergencyContactName: undefined,
        emergencyContactMobile: undefined,
        ...getCaregiverErrors(
          formData.emergencyContactName,
          val,
          hasSubmitted,
        ),
      }));
      return;
    }

    // Empty value
    if (!val) {
      setErrors((prev) => ({
        ...prev,
        [field]: undefined,
      }));
      return;
    }

    // Validate phone number
    const validPhone = isValidPhoneNumber(val);

    if (!validPhone) {
      setErrors((prev) => ({
        ...prev,
        [field]: t("patientForm.validation.mobileInvalid"),
      }));
      return;
    }

    // Clear validation error first
    setErrors((prev) => ({
      ...prev,
      [field]: undefined,
    }));

    // Only check duplicate for patient mobile number
    if (field === "mobileNumber") {
      try {
        const patientExists = await checkPatientExists({
          phone: val,
        }).unwrap();

        setErrors((prev) => ({
          ...prev,
          mobileNumber: patientExists
            ? t("patientForm.validation.mobileAlreadyExists")
            : undefined,
        }));
      } catch (error) {
        console.error("Failed to validate patient phone", error);
      }
    }
  };
  const handleDobCalendarOpenChange = (open: boolean) => {
    if (open) setDobCalendarMonth(formData.dateOfBirth ?? new Date());
    setDobCalendarOpen(open);
  };

  const handleDateOfBirthChange = (date: Date | undefined) => {
    if (!date) {
      setDobCalendarOpen(false);
      return;
    }
    // DOB and Age are mutually exclusive — picking a DOB clears any entered age.
    setFormData((prev) => ({ ...prev, dateOfBirth: date, age: "" }));
    if (errors.dateOfBirth || errors.age) {
      setErrors((prev) => ({ ...prev, dateOfBirth: undefined, age: undefined }));
    }
    setDobCalendarOpen(false);
  };

  const handleAgeChange = (value: string) => {
    // Digits only, capped at 3 characters (age is validated to 1-120 on submit).
    const nextAge = value.replace(/\D/g, "").slice(0, 3);
    // DOB and Age are mutually exclusive — entering an age clears any picked DOB.
    setFormData((prev) => ({
      ...prev,
      age: nextAge,
      dateOfBirth: undefined,
    }));
    if (errors.age || errors.dateOfBirth) {
      setErrors((prev) => ({ ...prev, age: undefined, dateOfBirth: undefined }));
    }
  };

  const handleSubmit = async () => {
    setHasSubmitted(true);

    if (!validateForm()) {
      return;
    }

    if (
      formData.appointmentDateTime &&
      isPastOrCurrentDateTime(formData.appointmentDateTime)
    ) {
      toast.error(t("patientForm.validation.appointmentFutureError"));
      return;
    }

    try {
      const payload: CreatePatientPayload = {
        first_name: formData.firstName,
        last_name: formData.lastName,
        gender: formData.gender,
        date_of_birth: formData.dateOfBirth
          ? toDateOnlyISO(formData.dateOfBirth)
          : formData.age.trim()
            ? toDateOnlyISO(getDateOfBirthFromAge(Number(formData.age)))
            : "",
        phone: formData.mobileNumber,
        email: formData.email.trim() || undefined,
        ethnicity: formData.ethnicity,
        caregiver_name: formData.emergencyContactName.trim() || undefined,
        caregiver_number: formData.emergencyContactMobile.trim() || undefined,
        recent_test_id: formData.recentTestId || undefined,
        group_ids: selectedGroups.map((g) => g.id),
        clinical_context_start_date: formData.appointmentDateTime
          ? toUTCISO(formData.appointmentDateTime)
          : toUTCISO(new Date()),
        clinical_context_end_date: (() => {
          const end = formData.appointmentDateTime
            ? new Date(formData.appointmentDateTime)
            : new Date();
          end.setMonth(end.getMonth() + 1);
          return toUTCISO(end);
        })(),
        recommended_doctors_ids: formData.assignedDoctor
          ? [formData.assignedDoctor]
          : [],
      };

      await createPatient(payload).unwrap();

      toast.success(t("addPatientDialog.success"));
      onSuccess?.();
      handleClose();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      toast.error(error?.data?.message || t("addPatientDialog.error"));
      console.error("Failed to create patient:", error);
    }
  };

  const handleClose = () => {
    setFormData(initialFormData);
    setErrors({});
    setHasSubmitted(false);
    setDobCalendarOpen(false);
    setDobCalendarMonth(new Date());
    setAppointmentCalendarOpen(false);
    setSelectedGroups([]);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="flex h-[calc(100dvh-1rem)] flex-col overflow-hidden rounded-xl sm:h-[700px] sm:max-w-[650px]">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold">
            {t("addPatientDialog.title")}
          </DialogTitle>
          <p className="text-sm text-gray-500 mt-1">
            {t("addPatientDialog.subtitle")}
          </p>
        </DialogHeader>

        <div
          className="min-h-0 flex-1 space-y-6 overflow-y-auto py-4 [&::-webkit-scrollbar]:hidden"
          style={{ scrollbarWidth: "none" }}
        >
          {/* Patient Details Section */}
          <div className="space-y-4">
            <h3 className="text-base font-medium text-gray-900 border-b pb-2">
              {t("addPatientDialog.patientInfoTitle")}
            </h3>

            <div className="space-y-4">
              {/* First Name */}
              <div className="space-y-2">
                <Label htmlFor="firstName" className="text-sm font-medium">
                  {t("patientForm.firstNameLabel")}{" "}
                  <span className="text-destructive-text">*</span>
                </Label>
                <Input
                  id="firstName"
                  placeholder={t("patientForm.firstNamePlaceholder")}
                  value={formData.firstName}
                  maxLength={NAME_MAX_LENGTH}
                  onChange={(e) =>
                    handleInputChange("firstName", e.target.value)
                  }
                  className={cn(
                    "w-full",
                    errors.firstName ? "border-destructive" : "",
                  )}
                />
                {errors.firstName && (
                  <p className="text-xs text-destructive-text">
                    {errors.firstName}
                  </p>
                )}
              </div>

              {/* Last Name */}
              <div className="space-y-2">
                <Label htmlFor="lastName" className="text-sm font-medium">
                  {t("patientForm.lastNameLabel")}{" "}
                  <span className="text-destructive-text">*</span>
                </Label>
                <Input
                  id="lastName"
                  placeholder={t("patientForm.lastNamePlaceholder")}
                  value={formData.lastName}
                  maxLength={NAME_MAX_LENGTH}
                  onChange={(e) =>
                    handleInputChange("lastName", e.target.value)
                  }
                  className={cn(
                    "w-full",
                    errors.lastName ? "border-destructive" : "",
                  )}
                />
                {errors.lastName && (
                  <p className="text-xs text-destructive-text">
                    {errors.lastName}
                  </p>
                )}
              </div>

              {/* Mobile Number */}
              <div className="space-y-2">
                <Label htmlFor="mobileNumber" className="text-sm font-medium">
                  {t("patientForm.mobileLabel")}{" "}
                  <span className="text-destructive-text">*</span>
                </Label>
                <div
                  className={cn(
                    "flex h-10 rounded-md border overflow-hidden",
                    errors.mobileNumber ? "border-destructive" : "border-input",
                  )}
                >
                  <PhoneInput
                    id="mobileNumber"
                    defaultCountry="IN"
                    international
                    countryCallingCodeEditable={false}
                    placeholder={t("patientForm.mobilePlaceholder")}
                    value={formData.mobileNumber}
                    onChange={(val) =>
                      handlePhoneChange("mobileNumber", val ?? "")
                    }
                  />
                </div>
                {errors.mobileNumber && (
                  <p className="text-xs text-destructive-text">
                    {errors.mobileNumber}
                  </p>
                )}
              </div>

              {/* Email */}
              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium">
                  {t("patientForm.emailLabel")}
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder={t("patientForm.emailPlaceholder")}
                  value={formData.email}
                  onChange={(e) => handleEmailChange(e.target.value)}
                  className={cn(
                    "w-full",
                    errors.email ? "border-destructive" : "",
                  )}
                />
                {errors.email && (
                  <p className="text-xs text-destructive-text">
                    {errors.email}
                  </p>
                )}
              </div>

              {/* Gender */}
              <div className="space-y-2">
                <Label htmlFor="gender" className="text-sm font-medium">
                  {t("patientForm.genderLabel")}{" "}
                  <span className="text-destructive-text">*</span>
                </Label>
                <Select
                  value={formData.gender}
                  onValueChange={(value) => handleInputChange("gender", value)}
                >
                  <SelectTrigger
                    id="gender"
                    className={cn(
                      "w-full",
                      errors.gender ? "border-destructive" : "",
                    )}
                  >
                    <SelectValue placeholder={t("patientForm.selectGender")} />
                  </SelectTrigger>
                  <SelectContent
                    position="popper"
                    side="bottom"
                    align="start"
                    sideOffset={4}
                    className="z-50"
                  >
                    {genderOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.gender && (
                  <p className="text-xs text-destructive-text">
                    {errors.gender}
                  </p>
                )}
              </div>

              {/* Ethnicity */}
              <div className="space-y-2">
                <Label htmlFor="ethnicity" className="text-sm font-medium">
                  {t("patientForm.ethnicityLabel")}{" "}
                  <span className="text-destructive-text">*</span>
                </Label>
                <Select
                  value={formData.ethnicity}
                  onValueChange={(value) =>
                    handleInputChange("ethnicity", value)
                  }
                >
                  <SelectTrigger
                    id="ethnicity"
                    className={cn(
                      "w-full",
                      errors.ethnicity ? "border-destructive" : "",
                    )}
                  >
                    <SelectValue placeholder={t("patientForm.selectEthnicity")} />
                  </SelectTrigger>
                  <SelectContent
                    position="popper"
                    side="bottom"
                    align="start"
                    sideOffset={4}
                    className="z-50"
                  >
                    {ethnicityOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.ethnicity && (
                  <p className="text-xs text-destructive-text">
                    {errors.ethnicity}
                  </p>
                )}
              </div>

              {/* Date of Birth / Age — mutually exclusive */}
              <div className="space-y-3 rounded-md border p-4">
                <div>
                  <p className="text-sm font-medium">
                    {t("patientForm.patientAgeLabel")}{" "}
                    <span className="text-destructive-text">*</span>
                  </p>
                  <p className="text-xs text-gray-500">
                    {t("patientForm.provideOneOf")}
                  </p>
                </div>

                <div className="space-y-2">
                  <Label
                    htmlFor="dateOfBirth"
                    className="text-sm font-medium"
                  >
                    {t("patientForm.dobLabel")}
                  </Label>
                  <Popover
                    open={dobCalendarOpen}
                    onOpenChange={handleDobCalendarOpenChange}
                  >
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        disabled={!!formData.age.trim()}
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !formData.dateOfBirth && "text-muted-foreground",
                          errors.dateOfBirth ? "border-destructive" : "",
                        )}
                        style={textStyles.body}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {formData.dateOfBirth ? (
                          format(formData.dateOfBirth, "dd/MM/yyyy")
                        ) : (
                          <span>{t("patientForm.dobPlaceholder")}</span>
                        )}
                      </Button>
                    </PopoverTrigger>

                    <PopoverContent className="w-auto p-3" align="start">
                      <div className="flex gap-2 mb-3">
                        <Select
                          value={String(dobCalendarMonth.getMonth())}
                          onValueChange={(month) => {
                            const newDate = new Date(dobCalendarMonth);
                            newDate.setMonth(Number(month));
                            setDobCalendarMonth(newDate);
                          }}
                        >
                          <SelectTrigger className="w-full sm:w-[120px]">
                            <SelectValue placeholder={t("patientForm.monthPlaceholder")} />
                          </SelectTrigger>
                          <SelectContent
                            className="w-[100px] max-h-60 overflow-y-auto"
                            position="popper"
                            side="bottom"
                            align="start"
                          >
                            {Array.from({ length: 12 }).map((_, i) => (
                              <SelectItem key={i} value={String(i)}>
                                {format(new Date(0, i), "MMM")}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>

                        <Select
                          value={String(dobCalendarMonth.getFullYear())}
                          onValueChange={(year) => {
                            const newDate = new Date(dobCalendarMonth);
                            newDate.setFullYear(Number(year));
                            setDobCalendarMonth(newDate);
                          }}
                        >
                          <SelectTrigger className="w-full sm:w-[100px]">
                            <SelectValue placeholder={t("patientForm.yearPlaceholder")} />
                          </SelectTrigger>
                          <SelectContent
                            className="w-[100px] max-h-60 overflow-y-auto"
                            position="popper"
                            side="bottom"
                            align="start"
                          >
                            {Array.from({ length: 100 }).map((_, i) => {
                              const year = new Date().getFullYear() - i;
                              return (
                                <SelectItem key={year} value={String(year)}>
                                  {year}
                                </SelectItem>
                              );
                            })}
                          </SelectContent>
                        </Select>
                      </div>

                      <Calendar
                        mode="single"
                        selected={formData.dateOfBirth}
                        onSelect={handleDateOfBirthChange}
                        month={dobCalendarMonth}
                        onMonthChange={setDobCalendarMonth}
                        disabled={(date) =>
                          date > new Date() || date < new Date("1900-01-01")
                        }
                        initialFocus
                        classNames={{
                          day_selected: "!bg-primary !text-white rounded-md",
                        }}
                      />
                    </PopoverContent>
                  </Popover>
                  {errors.dateOfBirth && (
                    <p className="text-xs text-destructive-text">
                      {errors.dateOfBirth}
                    </p>
                  )}
                </div>

                <div className="flex items-center gap-3">
                  <div className="h-px flex-1 bg-border" />
                  <span className="text-xs font-medium text-gray-500">
                    {t("patientForm.or")}
                  </span>
                  <div className="h-px flex-1 bg-border" />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="age" className="text-sm font-medium">
                    {t("patientForm.ageLabel")}
                  </Label>
                  <Input
                    id="age"
                    placeholder={t("patientForm.agePlaceholder")}
                    inputMode="numeric"
                    value={formData.age}
                    disabled={!!formData.dateOfBirth}
                    onChange={(e) => handleAgeChange(e.target.value)}
                    className={cn(
                      "w-full",
                      errors.age ? "border-destructive" : "",
                    )}
                  />
                  {errors.age && (
                    <p className="text-xs text-destructive-text">
                      {errors.age}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Emergency Contact Section */}
          <div className="space-y-4">
            <h3 className="text-base font-medium text-gray-900 border-b pb-2">
              {t("patientForm.emergencyContactTitle")}
            </h3>
            <p className="text-xs text-gray-500">
              {t("patientForm.emergencyContactHint")}
            </p>

            <div className="space-y-4">
              {/* Emergency Contact Name */}
              <div className="space-y-2">
                <Label
                  htmlFor="emergencyContactName"
                  className="text-sm font-medium"
                >
                  {t("patientForm.emergencyContactNameLabel")}
                </Label>
                <Input
                  id="emergencyContactName"
                  placeholder={t("patientForm.emergencyContactNameLabel")}
                  value={formData.emergencyContactName}
                  onChange={(e) =>
                    handleInputChange("emergencyContactName", e.target.value)
                  }
                  className={cn(
                    "w-full",
                    errors.emergencyContactName ? "border-destructive" : "",
                  )}
                />
                {errors.emergencyContactName && (
                  <p className="text-xs text-destructive-text">
                    {errors.emergencyContactName}
                  </p>
                )}
              </div>

              {/* Emergency Contact Mobile */}
              <div className="space-y-2">
                <Label
                  htmlFor="emergencyContactMobile"
                  className="text-sm font-medium"
                >
                  {t("patientForm.emergencyContactMobileLabel")}
                </Label>
                <div
                  className={cn(
                    "flex h-10 rounded-md border overflow-hidden",
                    errors.emergencyContactMobile
                      ? "border-destructive"
                      : "border-input",
                  )}
                >
                  <PhoneInput
                    id="emergencyContactMobile"
                    defaultCountry="IN"
                    international
                    countryCallingCodeEditable={false}
                    placeholder={t("patientForm.mobilePlaceholder")}
                    value={formData.emergencyContactMobile}
                    onChange={(val) =>
                      handlePhoneChange("emergencyContactMobile", val ?? "")
                    }
                  />
                </div>
                {errors.emergencyContactMobile && (
                  <p className="text-xs text-destructive-text">
                    {errors.emergencyContactMobile}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Assign Doctor Section */}
          <div className="space-y-4">
            <h3 className="text-base font-medium text-gray-900 border-b pb-2">
              {t("patientForm.assignDoctorTitle")}
            </h3>

            <div className="space-y-4">
              {/* Select Group */}
              <div className="space-y-2">
                <Label htmlFor="assignedGroup" className="text-sm font-medium">
                  {t("patientForm.selectGroupLabel")}
                </Label>
                <Select
                  value=""
                  onValueChange={addGroup}
                  disabled={isGroupsLoading}
                >
                  <SelectTrigger id="assignedGroup" className="w-full">
                    <SelectValue
                      placeholder={
                        isGroupsLoading
                          ? t("patientForm.loading")
                          : t("patientForm.addToGroup")
                      }
                    />
                  </SelectTrigger>

                  <SelectContent
                    position="popper"
                    side="bottom"
                    align="start"
                    sideOffset={4}
                    className="z-50"
                  >
                    {groups
                      .filter(
                        (g) => !selectedGroups.some((s) => s.id === g.id),
                      )
                      .map((group) => (
                        <SelectItem key={group.id} value={group.id}>
                          {group.name}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>

                {selectedGroups.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {selectedGroups.map((group) => (
                      <div
                        key={group.id}
                        className="flex items-center gap-1 px-2 py-1 bg-gray-100 rounded-md text-sm"
                      >
                        {group.name}
                        <button
                          type="button"
                          onClick={() => removeGroup(group.id)}
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Assign Doctor */}
              <div className="space-y-2">
                <Label htmlFor="assignedDoctor" className="text-sm font-medium">
                  {t("patientForm.assignDoctorLabel")}
                </Label>
                <Select
                  value={formData.assignedDoctor}
                  onValueChange={(value) =>
                    handleInputChange("assignedDoctor", value)
                  }
                >
                  <SelectTrigger id="assignedDoctor" className="w-full">
                    <SelectValue placeholder={t("patientForm.selectDoctor")} />
                  </SelectTrigger>
                  <SelectContent
                    position="popper"
                    side="bottom"
                    align="start"
                    sideOffset={4}
                    className="z-50"
                  >
                    {isDoctorsLoading ? (
                      <SelectItem value="loading" disabled>
                        {t("patientForm.loading")}
                      </SelectItem>
                    ) : groupDoctors.length === 0 ? (
                      <SelectItem value="empty" disabled>
                        {t("patientForm.noDoctors")}
                      </SelectItem>
                    ) : (
                      groupDoctors.map((doctor) => (
                        <SelectItem key={doctor.id} value={doctor.id}>
                          {doctor.name}
                          {doctor.role ? ` - ${doctor.role}` : ""}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>

              {/* Appointment Date & Time */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">
                  {t("patientForm.appointmentDateTimeLabel")}
                </Label>

                <Popover
                  open={appointmentCalendarOpen}
                  onOpenChange={setAppointmentCalendarOpen}
                >
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !formData.appointmentDateTime &&
                          "text-muted-foreground",
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {formData.appointmentDateTime ? (
                        format(
                          formData.appointmentDateTime,
                          "dd MMM yyyy, hh:mm a",
                        )
                      ) : (
                        <span>{t("patientForm.selectDateTime")}</span>
                      )}
                    </Button>
                  </PopoverTrigger>

                  <PopoverContent
                    className="w-auto p-4 space-y-4"
                    align="start"
                  >
                    <Calendar
                      mode="single"
                      selected={formData.appointmentDateTime}
                      onSelect={(date) => {
                        if (!date) return;
                        const current =
                          formData.appointmentDateTime || new Date();
                        const selectedDate = new Date(date);
                        selectedDate.setHours(
                          current.getHours(),
                          current.getMinutes(),
                        );
                        if (
                          isSameLocalDate(selectedDate) &&
                          isPastOrCurrentDateTime(selectedDate)
                        ) {
                          const [hours, minutes] = minimumFutureTime
                            .split(":")
                            .map(Number);
                          selectedDate.setHours(hours, minutes, 0, 0);
                        }
                        setFormData((prev) => ({
                          ...prev,
                          appointmentDateTime: selectedDate,
                        }));
                      }}
                      disabled={isBeforeLocalToday}
                      initialFocus
                      classNames={{
                        day: "h-9 w-9 p-0 font-normal",
                        day_selected: "!bg-primary !text-white rounded-md",
                        day_today: "border border-gray-400",
                        day_outside: "text-gray-300",
                      }}
                    />

                    <div className="flex items-center gap-2">
                      <input
                        type="time"
                        min={
                          isAppointmentDateToday ? minimumFutureTime : undefined
                        }
                        value={
                          formData.appointmentDateTime
                            ? formatTimeInputValue(formData.appointmentDateTime)
                            : ""
                        }
                        onChange={(e) => {
                          const value = getAllowedFutureTimeInputValue(
                            formData.appointmentDateTime || new Date(),
                            e.target.value,
                          );
                          if (!value) return;
                          const [hours, minutes] = value.split(":").map(Number);
                          const newDate = new Date(
                            formData.appointmentDateTime || new Date(),
                          );
                          newDate.setHours(hours, minutes);
                          setFormData((prev) => ({
                            ...prev,
                            appointmentDateTime: new Date(newDate),
                          }));
                        }}
                        className="border rounded-md px-2 py-1 text-sm"
                      />
                    </div>

                    <div className="flex justify-end">
                      <Button
                        size="sm"
                        onClick={() => setAppointmentCalendarOpen(false)}
                        className="h-10 cursor-pointer bg-blue-600 text-white hover:bg-blue-600"
                      >
                        {t("patientForm.done")}
                      </Button>
                    </div>
                  </PopoverContent>
                </Popover>

                {formData.appointmentDateTime && (
                  <p className="text-xs text-gray-500">
                    {format(formData.appointmentDateTime, "d MMM, h:mm a")}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="flex shrink-0 items-center justify-end gap-3 border-t pt-4">
          <Button variant="outline" onClick={handleClose} className="px-6">
            {t("patientForm.cancel")}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!isFormValid}
            loading={isLoading}
            className="px-6 bg-blue-600 hover:bg-blue-600 cursor-pointer text-white"
          >
            {t("addPatientDialog.add")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
